<template>
    <div>
        <div class="row">
            <div
                class="col-12 d-flex flex-column flex-md-row justify-content-between"
            >
                <div class="d-flex">
                    <h3 class="mr-4">Page builder</h3>
                    <FormKit
                        type="form"
                        :key="baseKey"
                        @submit="addComponent"
                        :actions="false"
                    >
                        <FormKit
                            type="select"
                            :options="componentOptions"
                            @change="addComponent"
                            name="component"
                        />
                    </FormKit>
                </div>

                <div class="mb-4 mb-md-0 pl-0 pl-md-4">
                    <button
                        type="button"
                        class="btn"
                        :class="[showPreview ? 'btn-danger' : 'btn-primary']"
                        v-if="previewAvailable"
                        @click="showPreview = !showPreview"
                        :disabled="loadingPreview"
                    >
                        <span v-if="loadingPreview">Preview laden...</span>
                        <span v-else>
                            {{
                                showPreview
                                    ? "Preview verstoppen"
                                    : "Preview laten zien"
                            }}
                        </span>
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary"
                        v-else
                        disabled
                    >
                        Preview niet beschikbaar door pagina grootte.
                    </button>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12 col-md mb-4">
                <div class="accordion" id="components">
                    <div
                        v-for="(component, index) in components"
                        :key="index"
                        class="card mb-0"
                        style="overflow: visible !important"
                    >
                        <div
                            class="card-header p-0 d-flex justify-content-between position-relative"
                            :id="`heading${index}`"
                        >
                            <h2 class="mb-0 w-100">
                                <button
                                    class="p-3 btn btn-link text-left w-100"
                                    type="button"
                                    data-toggle="collapse"
                                    :data-target="`#collapse${index}`"
                                    aria-expanded="true"
                                    :aria-controls="`collapse${index}`"
                                >
                                    # {{ index + 1 }} -
                                    {{
                                        baseComponents[component.component_type]
                                    }}
                                </button>
                            </h2>

                            <button
                                type="button"
                                @click="upperComponent(index)"
                                v-if="index !== 0"
                                class="btn btn-primary incremental-btn incremental-btn--up"
                            >
                                <i class="la la-chevron-up"></i>
                            </button>

                            <button
                                type="button"
                                @click="lowerComponent(index)"
                                v-if="index < components.length - 1"
                                class="btn btn-primary incremental-btn incremental-btn--down"
                            >
                                <i class="la la-chevron-down"></i>
                            </button>

                            <div class="d-flex align-items-center pr-2">
                                <button
                                    type="button"
                                    class="btn btn-danger"
                                    @click="deleteComponent(index)"
                                >
                                    <i class="lar la-trash-alt mr-2"></i>
                                </button>
                            </div>
                        </div>
                        <div
                            :id="`collapse${index}`"
                            class="collapse"
                            :aria-labelledby="`heading${index}`"
                            data-parent="#components"
                        >
                            <div class="card-body">
                                <component
                                    v-if="components[index]"
                                    :index="index"
                                    :is="component.component_type"
                                    v-model="components[index]"
                                    :key="component.uid"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="previewAvailable && showPreview" class="col-md-8">
                <iframe :src="url" :key="previewToken" class="w-100" style="height: 1200px" />
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import { v4 as uuidV4 } from "uuid";

export default {
    name: "Builder",
    props: {
        initialComponents: [],
    },
    data() {
        let initialComponents = Object.values(this.initialComponents);

        return {
            baseComponents: {
                PageHeader: "Header",
                PageMaisons: "Champagnehuizen overzicht",
                PageHotels: "Hotels overzicht",
                PageAlert: "Alert/melding",
                PageBanner: "Banner",
                PageNewsletter: "Nieuwsbrief formulier",
                PageRegions: "Regio overzicht",
                PageReviewSlider: "Reviews slider",
                PageUsps: "USPS / Belangrijke punten",
                PageSearchHero: "Homepagina Header",
                PageText: "Standaard text",
                PageDoubleGrid: "2 Kolommen met tekst",
                PageLogos: "Logo's",
                PageLinks: "SEO link lists",
            },
            baseKey: 1,
            components: [
                ...initialComponents.map((component) => ({
                    ...component,
                    uid: component.uid ?? this.uuid(),
                })),
            ],
            previewComponents: _.cloneDeep(initialComponents),
            previewTimeout: null,
            showPreview: true,
            previewToken: null,
            loadingPreview: false,
        };
    },
    methods: {
        deleteComponent(index) {
            this.components.splice(index, 1);
        },
        upperComponent(index) {
            let component = this.components.splice(index, 1)[0];
            this.components.splice(index - 1, 0, component);
        },
        lowerComponent(index) {
            let component = this.components.splice(index, 1)[0];
            this.components.splice(index + 1, 0, component);
        },
        addComponent(e) {
            let index = e.target.selectedIndex;
            let value = e.target.options[index].value;

            if (!value) {
                return;
            }

            this.components.push({
                uid: this.uuid(),
                component_type: value,
            });
            this.baseKey++;
        },
        uuid() {
            return uuidV4();
        },
        async generatePreviewToken() {
            try {
                this.loadingPreview = true;
                const apiUrl = process.env.MIX_API_URL || window.location.origin;
                
                const response = await fetch(`${apiUrl}/api/v1/preview/token`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({
                        components: this.previewComponents
                    })
                });
                
                if (!response.ok) {
                    throw new Error('Failed to generate preview token');
                }
                
                const data = await response.json();
                this.previewToken = data.token;
            } catch (error) {
                console.error('Error generating preview token:', error);
            } finally {
                this.loadingPreview = false;
            }
        },
    },
    mounted() {
        // Generate initial preview token
        this.generatePreviewToken();
    },
    watch: {
        components: {
            deep: true,
            handler(components) {
                clearTimeout(this.previewTimeout);
                this.previewTimeout = setTimeout(() => {
                    this.previewComponents = _.cloneDeep(components);
                }, 500);
            }
        },
        previewComponents: {
            handler() {
                // Debounce token generation
                clearTimeout(this.previewTimeout);
                this.previewTimeout = setTimeout(() => {
                    this.generatePreviewToken();
                }, 1000);
            },
            deep: true
        }
    },
    computed: {
        url() {
            return this.previewToken 
                ? `${process.env.MIX_PREVIEW_URL}?token=${this.previewToken}`
                : `${process.env.MIX_PREVIEW_URL}`;
        },
        previewAvailable() {
            // Always available now since we use tokens
            return true;
        },
        componentString() {
            return encodeURIComponent(JSON.stringify(this.previewComponents));
        },
        componentOptions() {
            return {
                "": "Selecteer component",
                ...this.baseComponents,
            };
        },
    },
};
</script>

<style>
.incremental-btn {
    position: absolute;
    left: -15px;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.incremental-btn i {
    font-size: 12px;
}

.incremental-btn--up {
    top: 5px;
}

.incremental-btn--down {
    bottom: 5px;
}
</style>
